/**
 * ResponseMap Component
 *
 * Renders a Leaflet map with circle markers showing where survey
 * responses were collected. Markers display city/region name and
 * response count in a popup. Auto-fits to data bounds.
 */

import React, { useEffect, useRef, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { buildPopupContent, extractLocations } from './responseMapUtils'

const MARKER_COLOR = '#0D9488'

export default function ResponseMap({ responses }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)

  const locations = useMemo(() => extractLocations(responses || []), [responses])

  useEffect(() => {
    if (!containerRef.current || locations.length === 0) return

    if (mapRef.current) {
      mapRef.current.remove()
      mapRef.current = null
    }

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      attributionControl: true,
    })
    mapRef.current = map

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
      maxZoom: 18,
    }).addTo(map)

    const maxCount = Math.max(...locations.map((l) => l.count))
    const bounds = []

    locations.forEach((loc) => {
      const radius = 8 + (loc.count / maxCount) * 22
      const marker = L.circleMarker([loc.lat, loc.lng], {
        radius,
        fillColor: MARKER_COLOR,
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.7,
      }).addTo(map)

      marker.bindPopup(buildPopupContent(loc), {
        closeButton: false,
        className: 'response-map-popup',
      })
      marker.on('mouseover', function () {
        this.openPopup()
      })
      marker.on('mouseout', function () {
        this.closePopup()
      })

      bounds.push([loc.lat, loc.lng])
    })

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 })
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [locations])

  if (locations.length === 0) return null

  return (
    <div className="response-map-card">
      <div className="response-map-header">Response Locations</div>
      <div ref={containerRef} className="response-map-container" />
    </div>
  )
}
